import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { 
  useUser, 
  useClerk, 
  useOrganization, 
  useOrganizationList,
  useAuth as useClerkAuth
} from '@clerk/clerk-react'
import { setAuthTokenGetter, setOrgIdGetter } from '../utils/api'

export type OrgRole = 'org:owner' | 'org:admin' | 'org:member'

interface OrganizationData {
  id: string
  name: string
  slug: string | null
  membersCount: number
  maxMembers: number
  role: OrgRole | null
  imageUrl: string | null
}

interface AuthContextType {
  user: {
    id: string
    email: string | null
    firstName: string | null
    lastName: string | null
    fullName: string | null
    imageUrl: string | null
  } | null
  loading: boolean
  signOut: () => Promise<void>
  error: Error | null
  isAuthEnabled: boolean
  isSignedIn: boolean
  currentOrg: OrganizationData | null
  orgRole: OrgRole | null
  organizations: OrganizationData[]
  switchOrganization: (orgId: string) => Promise<void>
  canInviteMembers: boolean
  canManageOrg: boolean
  isOrgLoading: boolean
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const isClerkConfigured = !!CLERK_PUBLISHABLE_KEY

const MAX_ORG_MEMBERS = 5

const devModeValue: AuthContextType = {
  user: null,
  loading: false,
  signOut: async () => {},
  error: null,
  isAuthEnabled: false,
  isSignedIn: false,
  currentOrg: null,
  orgRole: null,
  organizations: [],
  switchOrganization: async () => {},
  canInviteMembers: true,
  canManageOrg: true,
  isOrgLoading: false,
  getToken: async () => null,
}

function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser()
  const { signOut: clerkSignOut } = useClerk()
  const { getToken } = useClerkAuth()
  const { 
    organization: currentClerkOrg, 
    membership,
    isLoaded: isOrgLoaded 
  } = useOrganization()
  const { 
    userMemberships,
    setActive,
    isLoaded: isOrgListLoaded 
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  })
  
  const [error, setError] = useState<Error | null>(null)

  const loading = !isUserLoaded || (!isOrgLoaded && !!clerkUser)
  const isOrgLoading = !isOrgListLoaded

  const user = clerkUser ? {
    id: clerkUser.id,
    email: clerkUser.primaryEmailAddress?.emailAddress || null,
    firstName: clerkUser.firstName,
    lastName: clerkUser.lastName,
    fullName: clerkUser.fullName,
    imageUrl: clerkUser.imageUrl,
  } : null

  const orgRole = membership?.role as OrgRole | null

  const currentOrg: OrganizationData | null = currentClerkOrg ? {
    id: currentClerkOrg.id,
    name: currentClerkOrg.name,
    slug: currentClerkOrg.slug,
    membersCount: currentClerkOrg.membersCount || 0,
    maxMembers: MAX_ORG_MEMBERS,
    role: orgRole,
    imageUrl: currentClerkOrg.imageUrl || null,
  } : null

  // Get organizations from userMemberships.data
  const membershipList = userMemberships?.data || []
  const organizations: OrganizationData[] = membershipList.map((mem) => ({
    id: mem.organization.id,
    name: mem.organization.name,
    slug: mem.organization.slug,
    membersCount: mem.organization.membersCount || 0,
    maxMembers: MAX_ORG_MEMBERS,
    role: mem.role as OrgRole,
    imageUrl: mem.organization.imageUrl || null,
  }))

  const canInviteMembers = orgRole === 'org:owner' || orgRole === 'org:admin'
  const canManageOrg = orgRole === 'org:owner' || orgRole === 'org:admin'

  const signOut = async () => {
    try {
      setError(null)
      await clerkSignOut()
    } catch (err) {
      const error = err as Error
      setError(error)
      console.error('Error signing out:', error)
    }
  }

  const switchOrganization = async (orgId: string) => {
    try {
      setError(null)
      if (setActive) {
        await setActive({ organization: orgId })
      }
    } catch (err) {
      const error = err as Error
      setError(error)
      console.error('Error switching organization:', error)
    }
  }

  // Store current org ID in a ref so the getter always has the latest value
  const currentOrgIdRef = useRef<string | null>(null)
  
  // Store getToken in a ref so the auth getter always uses the latest function
  const getTokenRef = useRef(getToken)
  
  // Update refs whenever values change
  useEffect(() => {
    currentOrgIdRef.current = currentClerkOrg?.id || null
  }, [currentClerkOrg?.id])
  
  useEffect(() => {
    getTokenRef.current = getToken
  }, [getToken])

  const getAuthToken = async (): Promise<string | null> => {
    try {
      const currentGetToken = getTokenRef.current
      if (!currentGetToken) {
        console.error('Clerk getToken not available')
        return null
      }
      // Include organization ID in the token if user has an active organization
      // This ensures the org_id claim is present in the JWT for backend authorization
      const orgId = currentOrgIdRef.current
      if (orgId) {
        return await currentGetToken({ organizationId: orgId })
      }
      return await currentGetToken()
    } catch (err) {
      console.error('Error getting auth token:', err)
      return null
    }
  }
  
  // Set the auth token getter for the API utility once on mount
  // The getter uses refs internally, so it always has access to the latest values
  useEffect(() => {
    setAuthTokenGetter(getAuthToken)
    setOrgIdGetter(() => currentOrgIdRef.current)
  }, [])

  const value: AuthContextType = {
    user,
    loading,
    signOut,
    error,
    isAuthEnabled: true,
    isSignedIn: !!clerkUser,
    currentOrg,
    orgRole,
    organizations,
    switchOrganization,
    canInviteMembers,
    canManageOrg,
    isOrgLoading,
    getToken: getAuthToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function DevModeAuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <AuthContext.Provider value={devModeValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (isClerkConfigured) {
    return <ClerkAuthProvider>{children}</ClerkAuthProvider>
  }
  return <DevModeAuthProvider>{children}</DevModeAuthProvider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
